var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import './BasicMap';
// ── Flat palette per status ─────────────────────────────────────
const STATUS_FILL = {
    draft: '#64748B', // slate
    in_progress: '#F97316', // orange
    validated: '#10B981', // emerald
};
const DEFAULT_FILL = '#3B82F6';
const STATUS_KEYS = [
    'draft',
    'in_progress',
    'validated',
];
const SOURCE_ID = 'univers-map-source';
const LAYER_ID = 'univers-map-scenarios';
const IMAGE_PREFIX = 'univers-map-pin-';
const DEFAULT_IMAGE_ID = 'univers-map-pin-default';
// Same pin geometry as `<poi-map>` / `<scenario-map>`: viewBox 22.5×30
// rasterized into a 100×100 image. With `icon-anchor: 'bottom'`, the pin
// tip aligns to the geographic point.
const PIN_RASTER_SIZE = 100;
const PIN_VIEWBOX_HEIGHT = 30;
const PIN_BULB_CENTER_Y = 11.25;
const ICON_SIZE_DEFAULT = 0.5;
const ICON_SIZE_SELECTED = 0.6;
const COUNT_TEXT_SIZE = 13;
// Push the count text up into the bulb. With `text-anchor: 'center'` the
// text origin is the geo point (= pin tip after `icon-anchor: 'bottom'`).
// We need to shift it by the bulb's pixel offset above the tip:
//   pxAboveTip = (1 - bulbY / viewBoxHeight) * raster * iconSize
//   em        = -pxAboveTip / textSize
function bulbTextOffsetY(iconSize) {
    return (-((1 - PIN_BULB_CENTER_Y / PIN_VIEWBOX_HEIGHT) *
        PIN_RASTER_SIZE *
        iconSize) / COUNT_TEXT_SIZE);
}
const COUNT_TEXT_OFFSET_Y_DEFAULT = bulbTextOffsetY(ICON_SIZE_DEFAULT);
const COUNT_TEXT_OFFSET_Y_SELECTED = bulbTextOffsetY(ICON_SIZE_SELECTED);
/**
 * Map-pin SVG. Identical geometry to `<poi-map>` / `<scenario-map>` so
 * the three views feel like one product. The `change_count` is rendered
 * by a separate Mapbox `text-field` (offset to land inside the bulb)
 * rather than baked into the SVG, so the same image can be reused per
 * status without per-feature rasterization.
 */
function buildPinSvg(fill) {
    return `
    <svg xmlns="http://www.w3.org/2000/svg" width="${PIN_RASTER_SIZE}" height="${PIN_RASTER_SIZE}" viewBox="0 0 22.5 30">
      <path d="M11.25,0C5.04,0,0,5.04,0,11.25c0,2.68.91,5.28,2.59,7.37l8.66,11.38,8.66-11.38c1.67-2.09,2.59-4.69,2.59-7.37C22.5,5.04,17.46,0,11.25,0Z" fill="${fill}"/>
      <circle cx="11.25" cy="${PIN_BULB_CENTER_Y}" r="8" fill="#ffffff"/>
    </svg>
  `;
}
/**
 * UniversMap — renders one flat circle per scenario, anchored natively on
 * the map (no DOM markers, no drift on zoom/pan). The change count sits
 * inside the circle as a symbol `text-field`. Each scenario's `status`
 * picks the circle colour (draft / in_progress / validated).
 */
let UniversMap = class UniversMap extends LitElement {
    constructor() {
        super(...arguments);
        this.scenarios = [];
        this.mapOptions = {};
        this.autoFit = true;
        this.selectedScenarioId = null;
        this.mapInstance = null;
        this.mapLib = null;
        this.imagesLoaded = false;
        this.hasFitOnce = false;
        this.handlersBound = false;
        this.handleMapReady = async (event) => {
            this.mapInstance = event.detail.map;
            this.mapLib = event.detail.mapLib;
            // Kill drag inertia via the public pan-handler API.
            try {
                this.mapInstance?.dragPan?.enable?.({
                    linearity: 0.3,
                    easing: (t) => t,
                    maxSpeed: 0,
                    deceleration: 2500,
                });
            }
            catch {
                /* noop */
            }
            await this.loadImages();
            this.imagesLoaded = true;
            this.drawScenarios();
            if (this.autoFit && !this.hasFitOnce && this.scenarios.length > 0) {
                this.flyToScenarios();
            }
            this.bindClickHandlers();
            this.dispatchEvent(new CustomEvent('map-loaded', {
                detail: { map: this.mapInstance, mapLib: this.mapLib },
            }));
        };
    }
    updated(changed) {
        if (this.mapInstance &&
            this.imagesLoaded &&
            (changed.has('scenarios') || changed.has('selectedScenarioId'))) {
            this.drawScenarios();
            if (this.autoFit &&
                !this.hasFitOnce &&
                changed.has('scenarios') &&
                this.scenarios.length > 0) {
                this.flyToScenarios();
            }
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.cleanupMap();
    }
    getMapInstance() {
        return this.mapInstance;
    }
    getMapLib() {
        return this.mapLib;
    }
    flyToScenarios() {
        if (!this.mapInstance || !this.mapLib || this.scenarios.length === 0)
            return;
        const bounds = new this.mapLib.LngLatBounds();
        for (const s of this.scenarios) {
            bounds.extend([s.center.lng, s.center.lat]);
        }
        if (!bounds.isEmpty()) {
            this.mapInstance.fitBounds(bounds, { padding: 60 });
            this.hasFitOnce = true;
        }
    }
    loadImageFromSvg(imageId, svg) {
        return new Promise((resolve) => {
            const img = new Image();
            img.src =
                'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
            img.onload = () => {
                if (this.mapInstance.hasImage(imageId)) {
                    this.mapInstance.removeImage(imageId);
                }
                this.mapInstance.addImage(imageId, img, { sdf: false });
                resolve();
            };
            img.onerror = () => resolve();
        });
    }
    async loadImages() {
        if (!this.mapInstance)
            return;
        const jobs = [];
        for (const status of STATUS_KEYS) {
            jobs.push(this.loadImageFromSvg(IMAGE_PREFIX + status, buildPinSvg(STATUS_FILL[status])));
        }
        jobs.push(this.loadImageFromSvg(DEFAULT_IMAGE_ID, buildPinSvg(DEFAULT_FILL)));
        await Promise.all(jobs);
    }
    /**
     * Look up a bold font-stack in the currently loaded map style so the
     * count text renders reliably — same technique as `<poi-map>`.
     */
    getStyleFontStack(weight = 'bold') {
        const style = this.mapInstance?.getStyle?.();
        const targetWeight = weight === 'bold' ? /bold/i : /regular|normal/i;
        if (style?.layers) {
            for (const layer of style.layers) {
                const font = layer.layout?.['text-font'];
                if (font && Array.isArray(font)) {
                    const match = font.find((f) => targetWeight.test(f));
                    if (match)
                        return [match];
                }
            }
            for (const layer of style.layers) {
                const font = layer.layout?.['text-font'];
                if (font && Array.isArray(font))
                    return font;
            }
        }
        return ['Noto Sans Bold'];
    }
    buildFeatureCollection() {
        return {
            type: 'FeatureCollection',
            features: this.scenarios.map((s) => ({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [s.center.lng, s.center.lat],
                },
                properties: {
                    scenario_id: s.scenario_id,
                    change_count: s.change_count ?? 0,
                    status: s.status ?? null,
                    selected: s.scenario_id === this.selectedScenarioId,
                },
            })),
        };
    }
    /**
     * Idempotent layer setup: builds the source + symbol layer the first
     * time around and keeps them mounted afterwards. Style attributes
     * (size, offset) are expressed as `case`-on-`selected` so updates only
     * need to push fresh data into the source — no remove / re-add cycle
     * which used to flicker every time a scenario was selected.
     */
    ensureLayerMounted() {
        if (!this.mapInstance || !this.mapLib)
            return;
        if (this.mapInstance.getSource(SOURCE_ID))
            return;
        this.mapInstance.addSource(SOURCE_ID, {
            type: 'geojson',
            data: this.buildFeatureCollection(),
        });
        // icon-image expression: pick per-status pin, fall back to default
        // when the status is missing / unknown.
        const iconExpr = ['match', ['get', 'status']];
        for (const status of STATUS_KEYS) {
            iconExpr.push(status, IMAGE_PREFIX + status);
        }
        iconExpr.push(DEFAULT_IMAGE_ID);
        const fontStack = this.getStyleFontStack('bold');
        // Selected → bigger pin. The text offset has to grow in lock-step
        // so the count stays anchored to the bulb's center across states.
        const selectedExpr = ['boolean', ['get', 'selected'], false];
        const iconSizeExpr = [
            'case',
            selectedExpr,
            ICON_SIZE_SELECTED,
            ICON_SIZE_DEFAULT,
        ];
        const textOffsetExpr = [
            'case',
            selectedExpr,
            ['literal', [0, COUNT_TEXT_OFFSET_Y_SELECTED]],
            ['literal', [0, COUNT_TEXT_OFFSET_Y_DEFAULT]],
        ];
        this.mapInstance.addLayer({
            id: LAYER_ID,
            type: 'symbol',
            source: SOURCE_ID,
            layout: {
                'icon-image': iconExpr,
                'icon-size': iconSizeExpr,
                // Anchor the pin's tip to the geographic point.
                'icon-anchor': 'bottom',
                'icon-allow-overlap': true,
                'icon-ignore-placement': true,
                'text-field': ['to-string', ['get', 'change_count']],
                'text-font': fontStack,
                'text-size': COUNT_TEXT_SIZE,
                'text-anchor': 'center',
                'text-offset': textOffsetExpr,
                'text-allow-overlap': true,
                'text-ignore-placement': true,
            },
            paint: {
                'text-color': '#1f2937',
                'text-halo-color': '#ffffff',
                'text-halo-width': 0.6,
            },
        });
    }
    drawScenarios() {
        if (!this.mapInstance || !this.mapLib)
            return;
        this.ensureLayerMounted();
        const src = this.mapInstance.getSource?.(SOURCE_ID);
        if (src && typeof src.setData === 'function') {
            src.setData(this.buildFeatureCollection());
        }
    }
    bindClickHandlers() {
        if (!this.mapInstance || this.handlersBound)
            return;
        this.handlersBound = true;
        this.mapInstance.on('click', LAYER_ID, (e) => {
            const feature = e.features?.[0];
            if (!feature)
                return;
            const scenarioId = feature.properties?.scenario_id;
            const scenario = this.scenarios.find((s) => s.scenario_id === scenarioId);
            if (!scenario)
                return;
            this.dispatchEvent(new CustomEvent('scenario-selected', {
                detail: { scenario },
                bubbles: true,
                composed: true,
            }));
        });
        this.mapInstance.on('mouseenter', LAYER_ID, () => {
            this.mapInstance.getCanvas().style.cursor = 'pointer';
        });
        this.mapInstance.on('mouseleave', LAYER_ID, () => {
            this.mapInstance.getCanvas().style.cursor = '';
        });
    }
    cleanupMap() {
        if (!this.mapInstance)
            return;
        try {
            if (this.mapInstance.getLayer(LAYER_ID)) {
                this.mapInstance.removeLayer(LAYER_ID);
            }
            if (this.mapInstance.getSource(SOURCE_ID)) {
                this.mapInstance.removeSource(SOURCE_ID);
            }
        }
        catch {
            /* noop */
        }
    }
    render() {
        return html `
      <basic-map
        .mapOptions=${this.mapOptions}
        @map-loaded=${this.handleMapReady}
      >
        <slot></slot>
      </basic-map>
    `;
    }
};
UniversMap.styles = css `
    :host {
      display: block;
      width: 100%;
      height: 100%;
      position: relative;
    }
    basic-map {
      width: 100%;
      height: 100%;
      display: block;
    }
  `;
__decorate([
    property({ type: Array })
], UniversMap.prototype, "scenarios", void 0);
__decorate([
    property({ type: Object })
], UniversMap.prototype, "mapOptions", void 0);
__decorate([
    property({ type: Boolean, attribute: 'auto-fit' })
], UniversMap.prototype, "autoFit", void 0);
__decorate([
    property({ type: Number, attribute: 'selected-scenario-id' })
], UniversMap.prototype, "selectedScenarioId", void 0);
__decorate([
    state()
], UniversMap.prototype, "mapInstance", void 0);
UniversMap = __decorate([
    customElement('univers-map')
], UniversMap);
export { UniversMap };
//# sourceMappingURL=UniversMap.js.map